import type { AutocompleteProps, OptionType } from './index.types';
import React, { useRef, useState } from 'react';
import AsyncSelect from 'react-select';
import { cn } from '~/lib/utils';
import { AutocompleteClearIndicator } from './components/AutocompleteClearIndicator';
import { AutocompleteControl } from './components/AutocompleteControl';
import { AutocompleteNoOptionsMessage } from './components/AutocompleteNoOptionsMessage';
import { AutocompleteOption } from './components/AutocompleteOption';
import { AutocompleteValue } from './components/AutocompleteValue';
import { AutocompleteValueContainer } from './components/AutocompleteValueContainer';
import { customStyles } from './styles';
import { filterOption } from './utils';

/**
 * Multi-select autocomplete component with custom styling and filtering.
 */
export const Autocomplete = ({
  label,
  isMulti = true,
  options,
  loadOptions,
  value,
  onChange,
  onMenuScrollToBottom,
  placeholder = 'search',
  isClearable = true,
  isLoading = false,
  isDisabled = false,
  components = {},
  onInputChange,
  filterOption: customFilterOption,
  onKeyDown,
  focusedPlaceholder,
  testID,
  ...props
}: AutocompleteProps): React.ReactElement => {
  const internalMenuIsOpenRef = useRef(false);
  const { MultiValue, Option } = components;
  const [isFocused, setIsFocused] = useState(false);

  /**
   * Handle keydown events to prevent form submission when Enter is pressed
   * and the dropdown menu is closed
   */
  const handleKeyDown = (event: React.KeyboardEvent) => {
    onKeyDown?.(event);

    if (event.key === 'Enter' && !internalMenuIsOpenRef.current) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const componentTestId = testID ? `${testID}-autocomplete` : 'autocomplete';

  return (
    <div
      className={cn(
        'w-full min-w-0',
        isDisabled && 'opacity-50 cursor-not-allowed'
      )}
      data-testid={componentTestId}
    >
      <AsyncSelect
        label={label}
        options={options}
        loadOptions={loadOptions}
        value={value}
        isMulti={isMulti}
        isLoading={isLoading}
        isDisabled={isDisabled}
        isClearable={isClearable}
        filterOption={customFilterOption || filterOption}
        onChange={onChange}
        onInputChange={onInputChange}
        placeholder={placeholder}
        focusedPlaceholder={focusedPlaceholder}
        isSelectFocused={isFocused}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onMenuScrollToBottom={onMenuScrollToBottom}
        onMenuOpen={() => (internalMenuIsOpenRef.current = true)}
        onMenuClose={() => (internalMenuIsOpenRef.current = false)}
        onKeyDown={handleKeyDown}
        isSearchable={true}
        components={{
          Control: AutocompleteControl,
          MultiValue: MultiValue ?? AutocompleteValue,
          ClearIndicator: AutocompleteClearIndicator,
          Option: Option ?? AutocompleteOption,
          NoOptionsMessage: AutocompleteNoOptionsMessage,
          IndicatorSeparator: () => null,
          DropdownIndicator: () => null,
          ValueContainer: AutocompleteValueContainer,
        }}
        styles={customStyles(options?.length || 0)}
        classNamePrefix="react-select"
        getOptionLabel={(option: OptionType) => option.title}
        getOptionValue={(option: OptionType) => option.value}
        {...props}
      />
    </div>
  );
};
